import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  LabelWithIcon,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@pulse/ui';
import type { ComponentProps, ReactNode } from 'react';
import { useRef, useState } from 'react';

import { SearchField } from './SearchField';

type ChipPropsLite = ComponentProps<typeof Chip>;
// `Array.isArray` narrows to `any[]` per its lib.d.ts signature (`arg is any[]`), not to this
// union's actual array member — asserting back to it below keeps the merge below type-safe.
type ChipSxArray = Extract<NonNullable<ChipPropsLite['sx']>, readonly unknown[]>;

export interface SelectedChipProps {
  label: string;
  variant?: ChipPropsLite['variant'];
  sx?: ChipPropsLite['sx'];
}

// A selected-value Chip whose label can be arbitrarily long (e.g. a 100-char role/permission
// name) — caps the Chip to the row's width and wraps the label onto multiple lines (breaking
// mid-word if needed) instead of forcing the whole field into horizontal scroll. Exported so
// other features (e.g. UsersPage's role chips) can reuse the same wrapping behavior instead of
// duplicating this sx.
export function SelectedChip({ label, variant, sx }: SelectedChipProps) {
  const mergedSx = [
    {
      maxWidth: '100%',
      height: 'auto',
      '& .MuiChip-label': {
        display: 'block',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
        py: 0.5,
      },
    },
    ...(Array.isArray(sx) ? (sx as ChipSxArray) : sx ? [sx] : []),
  ] as ChipPropsLite['sx'];
  return <Chip label={label} size="small" variant={variant} sx={mergedSx} />;
}

export interface MultiSelectPickerProps<T> {
  label: string;
  /** Optional icon prepended to the visible field label (not the search field's own label or the
   *  placeholder/no-match text, which still derive from `label` alone) — rendered via
   *  `LabelWithIcon` (`inline`), so pass a bare icon element. Opt-in — omit for the existing
   *  plain-text label. */
  labelIcon?: ReactNode;
  options: readonly T[];
  value: readonly T[];
  onChange: (value: T[]) => void;
  getOptionLabel?: (option: T) => string;
  getOptionKey?: (option: T) => string;
  /**
   * Overrides the default read-only-TextField anchor with a custom trigger (e.g. a compact chip
   * row) — call the given `open` function from the trigger's onClick. The popover (search,
   * Select All, staged draft, Apply/Cancel) is unchanged either way. Omit for the default anchor.
   */
  renderAnchor?: (open: () => void) => ReactNode;
}

// A generic search + select-all + sticky header/footer popup for picking any list of options,
// anchored to a read-only text field. Built on Popover (not Select's Menu) because Menu's
// built-in focus/keyboard/type-ahead handling conflicts with an embedded search TextField.
// Selections are staged in local `draft` state and only committed to the parent via `onChange`
// on Apply — Cancel, backdrop click, and Escape all discard `draft` and leave `value` untouched.
// Equality/dedup is done via `getOptionKey`, not `===`, so `T` can be an object (e.g. IRole)
// rather than only a primitive.
export function MultiSelectPicker<T>({
  label,
  labelIcon,
  options,
  value,
  onChange,
  getOptionLabel = (option: T) => String(option),
  getOptionKey = getOptionLabel,
  renderAnchor,
}: MultiSelectPickerProps<T>) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<T[]>([...value]);
  const [search, setSearch] = useState('');
  const visibleLabel = labelIcon ? <LabelWithIcon icon={labelIcon} label={label} inline /> : label;

  const filtered = options.filter((o) =>
    getOptionLabel(o).toLowerCase().includes(search.trim().toLowerCase()),
  );
  const draftKeys = new Set(draft.map(getOptionKey));
  const allVisibleSelected = filtered.length > 0 && filtered.every((o) => draftKeys.has(getOptionKey(o)));
  const someVisibleSelected = filtered.some((o) => draftKeys.has(getOptionKey(o)));

  function handleOpen() {
    setDraft([...value]);
    setSearch('');
    setOpen(true);
  }

  function handleDiscard() {
    setOpen(false);
  }

  function handleApply() {
    onChange(draft);
    setOpen(false);
  }

  function toggleOne(o: T) {
    const key = getOptionKey(o);
    setDraft((prev) =>
      prev.some((p) => getOptionKey(p) === key) ? prev.filter((p) => getOptionKey(p) !== key) : [...prev, o],
    );
  }

  function toggleSelectAll() {
    if (allVisibleSelected) {
      const filteredKeys = new Set(filtered.map(getOptionKey));
      setDraft((prev) => prev.filter((p) => !filteredKeys.has(getOptionKey(p))));
    } else {
      setDraft((prev) => {
        const existingKeys = new Set(prev.map(getOptionKey));
        return [...prev, ...filtered.filter((o) => !existingKeys.has(getOptionKey(o)))];
      });
    }
  }

  return (
    <Box ref={anchorRef}>
      {renderAnchor ? (
        renderAnchor(handleOpen)
      ) : (
        <TextField
          label={visibleLabel}
          value=""
          placeholder={value.length === 0 ? `No ${label.toLowerCase()} selected` : undefined}
          onClick={handleOpen}
          fullWidth
          InputProps={{
            readOnly: true,
            startAdornment:
              value.length > 0 ? (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ py: 0.75, pr: '14px', maxWidth: '100%' }}>
                  {value.map((o) => (
                    <SelectedChip key={getOptionKey(o)} label={getOptionLabel(o)}  />
                  ))}
                </Stack>
              ) : undefined,
          }}
          sx={{
            '& .MuiInputBase-root': { flexWrap: 'wrap', cursor: 'pointer' },
            '& .MuiInputBase-input': { cursor: 'pointer', minWidth: 0, flex: value.length > 0 ? '0 0 0px' : 1 },
          }}
        />
      )}
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleDiscard}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box
          sx={{
            width: renderAnchor ? 320 : (anchorRef.current?.offsetWidth ?? 320),
            maxHeight: 420,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ p: 1.5, position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
            <SearchField
              placeholder={`Search ${label.toLowerCase()}…`}
              value={search}
              onChange={setSearch}
              fullWidth
              autoFocus
            />
            <FormControlLabel
              sx={{ mt: 1, ml: 0 }}
              control={
                <Checkbox
                  checked={allVisibleSelected}
                  indeterminate={!allVisibleSelected && someVisibleSelected}
                  onChange={toggleSelectAll}
                />
              }
              label="Select All"
            />
            <Divider />
          </Box>
          <Box sx={{ overflowY: 'auto', flex: 1, px: 1.5 }}>
            {filtered.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No {label.toLowerCase()} match your search.
              </Typography>
            ) : (
              <Stack>
                {filtered.map((o) => (
                  <FormControlLabel
                    key={getOptionKey(o)}
                    sx={{ ml: 0 }}
                    control={<Checkbox checked={draftKeys.has(getOptionKey(o))} onChange={() => toggleOne(o)} />}
                    label={getOptionLabel(o)}
                  />
                ))}
              </Stack>
            )}
          </Box>
          <Box sx={{ p: 1.5, position: 'sticky', bottom: 0, bgcolor: 'background.paper' }}>
            <Divider sx={{ mb: 1 }} />
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <IconButton
                aria-label="Cancel"
                onClick={handleDiscard}
              >
                <CloseIcon />
              </IconButton>
              <IconButton
                color="primary"
                aria-label="Apply"
                onClick={handleApply}
              >
                <CheckIcon />
              </IconButton>
            </Stack>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}
